<template>
  <div
    class="relative w-full flex flex-col items-center text-white bg-[#0f0f0f] mt-[20px] pt-[20px] lg:pt-[30px] pb-[20px]">

    <!-- ── Coming Soon Overlay (set comingSoon: false in data() to restore) ── -->
    <div v-if="comingSoon" class="relative z-10 flex flex-col items-center justify-center min-h-[80vh] w-full text-center px-6">
      <div class="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div class="absolute top-[10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-[#BC13FE] opacity-[0.15] blur-[140px]"></div>
        <div class="absolute bottom-[10%] right-[-5%] w-[380px] h-[380px] rounded-full bg-[#00F0FF] opacity-[0.12] blur-[130px]"></div>
      </div>
      <div class="relative z-10 flex flex-col items-center gap-6">
        <div class="w-24 h-24 rounded-full bg-gradient-to-br from-[#BC13FE]/30 to-[#00F0FF]/20 border border-[#00F0FF]/30 flex items-center justify-center shadow-[0_0_40px_rgba(0,240,255,0.2)]">
          <svg class="w-12 h-12 text-[#00F0FF]" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
          </svg>
        </div>
        <h1 class="text-4xl sm:text-5xl lg:text-6xl font-black tracking-widest uppercase bg-gradient-to-r from-[#c6f605] via-[#00F0FF] to-[#BC13FE] bg-clip-text text-transparent leading-tight">
          {{ $t('Common.ComingSoon') }}
        </h1>
        <p class="text-white/50 text-lg sm:text-xl font-semibold max-w-md">
          {{ $t('Common.WillBeAvailableSoon') }}
        </p>
        <div class="w-32 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#00F0FF] to-transparent"></div>
        <p class="text-white/30 text-sm">{{ $t('Rewards.Header') }}</p>
      </div>
    </div>

    <!-- ── Existing Content (hidden while comingSoon is true) ── -->
    <div v-else class="w-full">
    <!-- Scrollable Content Wrapper -->
    <div class="w-full h-full flex flex-col items-center justify-start">
      <!-- VIP Level Up Rewards Banner Section -->
      <section class="relative w-full max-w-[1300px] mx-auto px-4 pt-8 lg:pt-12">
        <div class="relative w-full h-auto min-h-[120px] py-6 sm:py-0 sm:h-[130px] lg:h-[140px] mb-4 overflow-hidden rounded-[20px] flex items-center shadow-xl bg-[#0d1225] border-x border-[#1e2535]">
          <!-- Background Image -->
          <img src="@/assets/revamp/promotion-hero-1.png" alt="Rewards Banner" class="absolute inset-0 w-full h-full object-cover object-center pointer-events-none z-0" />
          <div class="absolute inset-0 bg-[#060b18]/20 pointer-events-none z-[1]"></div>
          <div class="absolute inset-0 bg-gradient-to-r from-[#060b18]/85 via-[#060b18]/30 to-transparent pointer-events-none z-[1]"></div>
          
          <!-- Text Content -->
          <div class="relative z-[2] w-full px-6 sm:pl-8 md:pl-10 lg:pl-12">
            <h1 class="text-[28px] sm:text-[36px] md:text-4xl lg:text-[42px] font-black tracking-wider uppercase font-sans text-white m-0 leading-[1.1] drop-shadow-md">
              {{ $t('Rewards.Header') }}
            </h1>
            <p class="text-[12px] sm:text-[14px] md:text-[15px] text-gray-300 mt-2 font-medium drop-shadow-sm max-w-[90%]">
              {{ $t('Rewards.Subtext') }}
            </p>
          </div>
        </div>
      </section>

    <!-- REWARDS LIST SECTION -->
    <section class="w-full max-w-[1300px] mx-auto px-4 pb-20 mt-0 relative z-20">
      <div class="flex flex-col gap-4">
        
        <!-- Tier Row v-for Loop -->
        <div 
          v-for="(tier, index) in tiers" 
          :key="index"
          class="w-full rounded-[16px] border border-white/5 flex flex-col md:flex-row items-center justify-between p-6 md:py-8 md:px-10 gap-6 transition-all duration-300 bg-[#121212] hover:bg-gradient-to-r hover:from-[#BC13FE] hover:to-[#0A8E97] hover:border-[#00F0FF]/30 hover:shadow-[0_0_25px_rgba(0,240,255,0.15)]"
        >
          <!-- 1. Left Section (Icon + Name) -->
          <div class="flex items-center gap-4 w-full md:w-1/3">
            <!-- Squared Icon with specific bg -->
            <div 
              class="w-16 h-16 rounded-[12px] flex items-center justify-center flex-shrink-0"
              :class="tier.iconBg"
            >
              <img :src="tier.icon" :alt="tier.name" class="w-10 h-10 object-contain" />
            </div>
            <!-- Name -->
            <div class="flex flex-col items-start">
              <span class="text-lg md:text-xl font-bold text-white tracking-wide font-sans">
                {{ tier.name }}
              </span>
            </div>
          </div>

          <!-- 2. Wager Section -->
          <div class="flex flex-col items-start md:items-center w-full md:w-1/3 gap-1">
            <span class="text-[10px] md:text-xs font-extrabold tracking-widest uppercase font-sans text-gray-500">
              {{ $t('Rewards.RequiredWager') }}
            </span>
            <span class="text-lg md:text-2xl font-black text-white font-sans">
              {{ tier.wager }}
            </span>
          </div>

          <!-- 3. Reward Section -->
          <div class="flex flex-col items-start md:items-center w-full md:w-1/3 gap-1">
            <span class="text-[10px] md:text-xs font-extrabold tracking-widest uppercase font-sans text-gray-500">
              {{ $t('Rewards.LevelReward') }}
            </span>
            <span class="text-lg md:text-2xl font-black text-[#00F0FF] font-sans">
              {{ tier.reward }}
            </span>
          </div>



        </div>
        
      </div>
    </section>
  </div>
</div>
    </div><!-- end v-else -->
</template>

<script>
import { defineComponent } from "vue";

// Rank Icons
import bronze from "@/assets/icons/bronze.png";
import silver from "@/assets/icons/silver.png";
import gold from "@/assets/icons/gold.png";
import platinum1 from "@/assets/icons/platinum1.png";
import platinum2 from "@/assets/icons/platinum2.png";
import platinum3 from "@/assets/icons/platinum3.png";
import platinum4 from "@/assets/icons/platinum4.png";
import platinum5 from "@/assets/icons/platinum5.png";

export default defineComponent({
  name: "RewardsPage",
  data() {
    return {
      // Set to false to restore the rewards page
      comingSoon: true,

      tiers: [
        { name: "Bronze", wager: "$10,000", reward: "$15.00", status: "claimed", icon: bronze, iconBg: "bg-[#B87333]/20 border border-[#B87333]/40" },
        { name: "Silver", wager: "$50,000", reward: "$50.00", status: "active", icon: silver, iconBg: "bg-[#8E8E8E]/20 border border-[#8E8E8E]/40" },
        { name: "Gold", wager: "$100,000", reward: "$75.00", status: "locked", icon: gold, iconBg: "bg-[#FFD700]/20 border border-[#FFD700]/40" },
        { name: "Platinum I", wager: "$250,000", reward: "$150.00", status: "locked", icon: platinum1, iconBg: "bg-[#3A9ACD]/20 border border-[#3A9ACD]/40" },
        { name: "Platinum II", wager: "$500,000", reward: "$250.00", status: "locked", icon: platinum2, iconBg: "bg-[#3A9ACD]/20 border border-[#3A9ACD]/40" },
        { name: "Platinum III", wager: "$1,000,000", reward: "$500.00", status: "locked", icon: platinum3, iconBg: "bg-[#3A9ACD]/20 border border-[#3A9ACD]/40" },
        { name: "Platinum IV", wager: "$2,500,000", reward: "$1,500.00", status: "locked", icon: platinum4, iconBg: "bg-[#3A9ACD]/20 border border-[#3A9ACD]/40" },
        { name: "Platinum V", wager: "$5,000,000", reward: "$2,500.00", status: "locked", icon: platinum5, iconBg: "bg-[#3A9ACD]/20 border border-[#3A9ACD]/40" },
      ]
    };
  }
});
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scroll-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
